import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { StepPlan } from "../../../api/content/stepPlans";
import type { ThreadDetail } from "../../../api/threads/liveMessages";
import { WORKFLOW_GRAPH_DENSITIES, WORKFLOW_GRAPH_LAYOUTS, type WorkflowGraphDensity, type WorkflowGraphLayout } from "../../../api/userPreferencesApi";
import AppButton from "../../../ui/buttons/AppButton";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import { editIntent, useHerePath, useThreadPreparation, type ItemAtVersion } from "../../threads/prepare";
import type { LoadedProcess } from "../data/processCatalogue";
import GraphViewChoice from "../graph/GraphViewChoice";
import GraphLegend from "../graph/GraphLegend";
import type { CanvasSizing } from "../graph/WorkflowCanvas";
import { GraphActionsContext, type GraphActions } from "../graph/graphActions";
import { graphRun } from "../model/graphRun";
import { runCardLines, type RunWaits } from "../graph/runLines";
import { placementAxes } from "../model/placementAxes";
import { expandableNodeIds, workflowGraph } from "../model/workflowGraph";
import WorkflowOutline from "../outline/WorkflowOutline";
import { PROCESS_DISPLAYS, type ProcessDisplay } from "./processDisplay";

interface ProcessViewProps {
  process: LoadedProcess;
  /** The Workflow's step plans before anything ran, by flat step key (`useWorkflowPlans`): each card's axes. */
  plans: ReadonlyMap<string, StepPlan>;
  display: ProcessDisplay;
  onDisplayChange: (display: ProcessDisplay) => void;
  /** How much the cards show: the view's own choice (F76). */
  density: WorkflowGraphDensity;
  onDensityChange: (density: WorkflowGraphDensity) => void;
  /** Which way the steps run: the view's own choice too (F59, as F76). */
  layout: WorkflowGraphLayout;
  onLayoutChange: (layout: WorkflowGraphLayout) => void;
  /**
   * On the run graph: the Run's step and group detail from module C (W-22, WD-4), and what its waiting step waits for,
   * in the Run's words; `null` on the Workflow graph.
   */
  run: { readonly detail: ThreadDetail; readonly waits: RunWaits } | null;
  /** On the run graph: where a step's state opens the Run view, or `null` while it has none. */
  stepLinkInThread: ((progressKey: string) => string | null) | null;
  /** How tall the graph's canvas is (`WorkflowCanvas`). */
  canvas: CanvasSizing;
  /**
   * The project the view works in: the context map reads its content, and a card's Edit
   * opens its edit Thread there. Without it the view offers neither.
   */
  contextProjectId?: string | null;
}

const NOTHING = new Set<string>();

// React Flow loads with the first graph shown, not with the app.
const WorkflowCanvas = lazy(() => import("../graph/WorkflowCanvas"));
const ContextGraphView = lazy(() => import("../context/ContextGraphView"));

/**
 * A Workflow as the user first sees it: the main pipeline, every input closed (P-14), or
 * every input open at the detailed density. The user opens inputs one by one, or all at
 * once, opens a card on its settings, and can switch to the list view.
 */
export default function ProcessView({ process, plans, display, onDisplayChange, density, onDensityChange, layout: orientation, onLayoutChange, run: shownRun, stepLinkInThread, canvas, contextProjectId = null }: ProcessViewProps) {
  const { t } = useTranslation();
  const allExpandable = useMemo(() => expandableNodeIds(process.workflow, process.catalogue), [process]);
  const [expandedByUser, setExpandedByUser] = useState<ReadonlySet<string> | null>(null);
  // Until the user opens or closes an input, the density decides: all open when detailed.
  const startOpen = density === "detailed";
  const expanded = expandedByUser ?? (startOpen ? allExpandable : NOTHING);
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  const [showContext, setShowContext] = useState(false);
  const preparation = useThreadPreparation();
  const here = useHerePath();
  const graph = useMemo(() => workflowGraph(process.workflow, process.catalogue, expanded), [process, expanded]);
  const detail = shownRun?.detail ?? null;
  const waits = shownRun?.waits ?? null;
  const run = useMemo(() => (detail === null ? null : graphRun(graph, detail)), [graph, detail]);
  const runLines = useMemo(() => (run === null || waits === null ? undefined : runCardLines(run, waits, t)), [run, waits, t]);
  const axes = useMemo(() => placementAxes(graph, plans), [graph, plans]);

  const toggle = useCallback(
    (nodeId: string) => {
      setExpandedByUser((current) => toggled(current ?? (startOpen ? allExpandable : NOTHING), nodeId));
    },
    [startOpen, allExpandable],
  );
  const toggleCard = useCallback((nodeId: string) => {
    setOpened((current) => toggled(current, nodeId));
  }, []);
  const edit = useMemo(
    () => (contextProjectId === null ? null : (item: ItemAtVersion) => { preparation.prepare(editIntent(contextProjectId, item, here)); }),
    [contextProjectId, preparation, here],
  );
  const actions = useMemo<GraphActions>(() => ({ toggle, toggleCard, stepLinkInThread, edit }), [toggle, toggleCard, stepLinkInThread, edit]);
  const allOpen = allExpandable.size > 0 && [...allExpandable].every((id) => expanded.has(id));

  return (
    <GraphActionsContext.Provider value={actions}>
      <div className="app-process-view space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="app-page-switcher app-flow-display-tabs flex" role="tablist" aria-label={t("workflows.view.display")}>
            {PROCESS_DISPLAYS.map((candidate) => (
              <button
                key={candidate}
                type="button"
                role="tab"
                aria-selected={candidate === display}
                className="app-page-switcher-button px-3 py-1 text-xs"
                onClick={() => {
                  onDisplayChange(candidate);
                }}
              >
                {t(`workflows.view.displays.${candidate}`)}
              </button>
            ))}
          </div>
          {display === "graph" && contextProjectId !== null ? (
            <AppButton
              variant={showContext ? "primary" : "secondary"}
              size="xs"
              aria-pressed={showContext}
              onClick={() => {
                setShowContext(!showContext);
              }}
            >
              {t("workflows.context.toggle")}
            </AppButton>
          ) : null}
          {allExpandable.size === 0 ? null : (
            <AppButton
              variant="secondary"
              size="xs"
              onClick={() => {
                setExpandedByUser(allOpen ? new Set() : allExpandable);
              }}
            >
              {allOpen ? t("workflows.view.collapseAll") : t("workflows.view.expandAll")}
            </AppButton>
          )}
          {display === "graph" ? <GraphViewChoice kind="density" choices={WORKFLOW_GRAPH_DENSITIES} value={density} onChange={onDensityChange} /> : null}
          {display === "graph" ? <GraphViewChoice kind="layout" choices={WORKFLOW_GRAPH_LAYOUTS} value={orientation} onChange={onLayoutChange} /> : null}
          {display === "graph" ? <GraphLegend /> : null}
        </div>
        {display === "graph" ? (
          <Suspense fallback={<LoadingIndicator label={t("workflows.view.loading")} className="py-10" />}>
            {showContext && contextProjectId !== null ? (
              <ContextGraphView projectId={contextProjectId} process={process} graph={graph} axes={axes} canvas={canvas} density={density} orientation={orientation} opened={opened} />
            ) : (
              <WorkflowCanvas graph={graph} catalogue={process.catalogue} run={run} axes={axes} className="app-card overflow-hidden" sizing={canvas} density={density} orientation={orientation} opened={opened} cardLines={runLines} stepLinkInThread={stepLinkInThread} />
            )}
          </Suspense>
        ) : (
          <WorkflowOutline graph={graph} run={run} axes={axes} />
        )}
      </div>
    </GraphActionsContext.Provider>
  );
}

function toggled(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  if (!next.delete(id)) {
    next.add(id);
  }
  return next;
}
